"use client"

import { useState, useRef, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import {
    Users,
    Linkedin,
    Twitter,
    Instagram,
    Youtube, // Import Youtube
    Globe,
    MessageCircle,
    ChevronUp
} from "lucide-react"
import { cn } from "@/lib/utils"
import Tooltip from "@mui/material/Tooltip"

export const TwitterIcon = ({ className }: { className?: string }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path>
    </svg>
)

export const WhatsAppIcon = ({ className }: { className?: string }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"></path>
    </svg>
)

export function SocialsDropup({ isCollapsed }: { isCollapsed?: boolean }) {
    const [isOpen, setIsOpen] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)
    const buttonRef = useRef<HTMLButtonElement>(null)

    // Close on click outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false)
            }
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [])

    const socialLinks = [
        {
            name: "WhatsApp",
            icon: WhatsAppIcon,
            href: "https://chat.whatsapp.com/B9gMJ4FvMNHGIgKQMux1zq",
            color: "text-slate-400 group-hover:text-emerald-600",
            hoverBg: ""
        },
        {
            name: "LinkedIn",
            icon: Linkedin,
            href: "https://www.linkedin.com/company/deckwale",
            color: "text-slate-400 group-hover:text-blue-600",
            hoverBg: ""
        },
        {
            name: "Twitter / X",
            icon: TwitterIcon,
            href: "https://x.com/Deckwale",
            color: "text-slate-400 group-hover:text-slate-900",
            hoverBg: ""
        },
        {
            name: "Instagram",
            icon: Instagram,
            href: "https://www.instagram.com/deckwale",
            color: "text-slate-400 group-hover:text-pink-600",
            hoverBg: ""
        },
        {
            name: "Youtube",
            icon: Youtube,
            href: "https://www.youtube.com/@DeckwaleAI",
            color: "text-slate-400 group-hover:text-red-600",
            hoverBg: ""
        }
    ]

    const TriggerButton = (
        <button
            ref={buttonRef}
            onClick={() => setIsOpen(!isOpen)}
            className={cn(
                "cursor-pointer flex border-none items-center transition-all duration-200 group border",
                isCollapsed
                    ? "w-9 h-9 justify-center rounded-lg hover:bg-white border-transparent hover:border-slate-200 overflow-hidden"
                    : "w-full gap-3 px-2.5 py-1.5 rounded-lg whitespace-nowrap overflow-hidden",
                isOpen
                    ? "text-slate-900"
                    : "text-slate-700 hover:text-slate-900"
            )}
        >
            <div className={cn(
                "flex items-center justify-center transition-all duration-200",
                !isCollapsed && "rounded-lg",
            )}>
                <Users className={cn("flex-shrink-0", isCollapsed ? "w-4 h-4 text-slate-800 group-hover:text-slate-900" : "w-3.5 h-3.5 text-slate-400 "   )} />
            </div>

            {!isCollapsed && (
                <>
                    <div className="flex-1 flex items-center justify-between text-left overflow-hidden">
                        <span className="text-[13px] font-medium truncate">Socials</span>
                        <ChevronUp className={cn(
                            "w-3 h-3 transition-transform duration-300 opacity-50 flex-shrink-0",
                            isOpen ? "rotate-180" : ""
                        )} />
                    </div>
                </>
            )}
        </button>
    )

    if (isCollapsed) {
        return (
            <div className={cn("relative", "w-full flex justify-center")}>
                <AnimatePresence>
                    {isOpen && (
                        <motion.div
                            ref={menuRef}
                            initial={{ opacity: 0, x: -10, scale: 0.95 }}
                            animate={{ opacity: 1, x: 0, scale: 1 }}
                            exit={{ opacity: 0, x: -10, scale: 0.95 }}
                            transition={{ duration: 0.2, ease: "easeOut" }}
                            className="absolute left-full bottom-0 ml-4 mb-[-10px] bg-white rounded-xl border border-slate-100 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.1)] z-50 overflow-hidden min-w-[200px]"
                        >
                            <div className="text-[12px] font-medium text-slate-400 px-3 py-2">
                                Connect with us
                            </div>
                            <div className="grid grid-cols-1 gap-0.5">
                                {socialLinks.map((social) => (
                                    <a
                                        key={social.name}
                                        href={social.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={cn(
                                            "flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 group",
                                            social.hoverBg
                                        )}
                                    >
                                        <social.icon className={cn("w-3 h-3 transition-colors", social.color)} />
                                        <span className="text-xs font-medium text-slate-600 group-hover:text-slate-900">
                                            {social.name}
                                        </span>
                                    </a>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
                <Tooltip title="Socials" placement="right" arrow>
                    {TriggerButton}
                </Tooltip>
            </div>
        )
    }

    return (
        <div className="relative w-full">
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        ref={menuRef}
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="absolute bottom-full left-0 w-full mb-2 p-1.5 bg-white rounded-xl border border-slate-100 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.1)] z-50 overflow-hidden"
                    >
                        {/* Mobile/Expanded Menu Content */}
                        <div className="text-[12px] font-medium text-slate-400 px-3 py-2">
                            Connect with us
                        </div>
                        <div className="grid grid-cols-1 gap-0.5">
                            {socialLinks.map((social) => (
                                <a
                                    key={social.name}
                                    href={social.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={cn(
                                        "flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 group",
                                        social.hoverBg
                                    )}
                                >
                                    <social.icon className={cn("w-3 h-3 transition-colors", social.color)} />
                                    <span className="text-xs font-medium text-slate-600 group-hover:text-slate-900">
                                        {social.name}
                                    </span>
                                </a>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
            {TriggerButton}
        </div>
    )
}
